import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { RoutePaths } from "@/core/constants/RoutePaths";
import { useUserSector } from "@/core/hooks/useUserSector";
import { useSectorAccessState } from "@/core/hooks/useSectorAccessState";
import { resolveBillingGate } from "@/features/billing/utils/resolveBillingGate";

type Props = { children: ReactNode };

/**
 * Guarda as rotas do produto (dentro do MainLayout). Não renderiza nenhuma tela
 * de bloqueio inline — quando o billing trava o acesso, **redireciona** para a
 * página dedicada de gate (`PLAN_GATE`), que ocupa a tela inteira. Assim o
 * paywall/onboarding não fica espremido no conteúdo ao lado do header/sidebar.
 */
export function SectorAccessGuard({ children }: Props) {
  const { activeSectorId, isActiveSectorReady, isDevAccessActive } =
    useUserSector();
  const { data: access, isSuccess } = useSectorAccessState(activeSectorId);

  // Dev na conta de um cliente (SA-1482) não é quem paga e precisa ver o
  // produto como está; a tela de planos ainda o prenderia lá, sem switcher.
  // Só dá para saber que o acesso é de dev depois que a lista de contas
  // confirma a conta ativa: antes disso, não decide (num F5 o access-state
  // pode chegar primeiro e mandar o dev para o paywall).
  if (!isActiveSectorReady || isDevAccessActive) {
    return <>{children}</>;
  }

  // Só decidimos o gate com o access-state REALMENTE carregado para o sector
  // ativo. Enquanto não houver sector resolvido ou o fetch não tiver concluído,
  // libera o conteúdo (fail-open) SEM redirecionar — redirecionar no estado
  // transitório (sector ainda nulo → fail-open) causaria ping-pong com a
  // página de gate.
  if (!activeSectorId || !isSuccess) {
    return <>{children}</>;
  }

  const gate = resolveBillingGate(access);
  if (gate.kind !== "allow") {
    return (
      <Navigate
        to={RoutePaths.PLAN_GATE}
        replace
      />
    );
  }

  return <>{children}</>;
}
